/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {ComponentFactoryResolver, ComponentRef} from '@angular/core';

import {RouterOutlet} from './directives/router_outlet';
import {ActivatedRoute} from './router_state';
/**
 * Store contextual information about a {\@link RouterOutlet}
 * 
 * \@stable
 */
export class OutletContext {
  outlet: RouterOutlet|null = null;
  route: ActivatedRoute|null = null;
  resolver: ComponentFactoryResolver|null = null;
  children = new ChildrenOutletContexts();
  attachRef: ComponentRef<any>|null = null;
}

function OutletContext_tsickle_Closure_declarations() {
/** @type {?} */
OutletContext.prototype.outlet;
/** @type {?} */
OutletContext.prototype.route;
/** @type {?} */
OutletContext.prototype.resolver;
/** @type {?} */
OutletContext.prototype.children;
/** @type {?} */
OutletContext.prototype.attachRef;
}

/**
 * Store contextual information about the children (= nested) {\@link RouterOutlet}
 * 
 * \@stable
 */
export class ChildrenOutletContexts {
private contexts = new Map<string, OutletContext>();
/**
 * Called when a `RouterOutlet` directive is instantiated
 * @param {?} childName
 * @param {?} outlet
 * @return {?}
 */
onChildOutletCreated(childName: string, outlet: RouterOutlet): void {
    const /** @type {?} */ context = this.getOrCreateContext(childName);
    context.outlet = outlet;
    this.contexts.set(childName, context);
  }
/**
 * Called when a `RouterOutlet` directive is destroyed.
 * We need to keep the context as the outlet could be destroyed inside a NgIf and might be
 * re-created later.
 * @param {?} childName
 * @return {?}
 */
onChildOutletDestroyed(childName: string): void {
    const /** @type {?} */ context = this.getContext(childName);
    if (context) {
      context.outlet = null;
    }
  }
/**
 * Called when the corresponding route is deactivated during navigation.
 * Because the component get destroyed, all children outlet are destroyed.
 * @return {?}
 */
onOutletDeactivated(): Map<string, OutletContext> {
    const /** @type {?} */ contexts = this.contexts;
    this.contexts = new Map();
    return contexts;
  }
/**
 * @param {?} contexts
 * @return {?}
 */
onOutletReAttached(contexts: Map<string, OutletContext>) { this.contexts = contexts; }
/**
 * @param {?} childName
 * @return {?}
 */
getOrCreateContext(childName: string): OutletContext {
    let /** @type {?} */ context = this.getContext(childName);

    if (!context) {
      context = new OutletContext();
      this.contexts.set(childName, context);
    }

    return context;
  }
/**
 * @param {?} childName
 * @return {?}
 */
getContext(childName: string): OutletContext|null { return this.contexts.get(childName) || null; }
}

function ChildrenOutletContexts_tsickle_Closure_declarations() {
/** @type {?} */
ChildrenOutletContexts.prototype.contexts;
}




// WEBPACK FOOTER //
// ../../../../packages/router/src/router_outlet_context.ts